import { useEffect, useState, useCallback } from 'react';

declare global {
  interface Navigator {
    standalone?: boolean;
  }
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<{ outcome: 'accepted' | 'dismissed' } | void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'android' | 'ios' | 'desktop' | 'unknown';

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  // iPadOS 13+ reports as Macintosh with touch support
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Windows|Mac|Linux/.test(ua) && !/Mobile/.test(ua)) return 'desktop';
  return 'unknown';
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigator.standalone === true // Safari iOS
  );
}

function getStorage(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function setStorage(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* preference not saved */ }
}

const BANNER_DISMISSED_KEY = 'qops-install-banner-dismissed';

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(isStandalone);
  const [showBanner, setShowBanner] = useState(false);
  const [platform] = useState(detectPlatform);

  useEffect(() => {
    if (isInstalled) return;

    const handler = (e: Event) => {
      e.preventDefault();
      // Runtime guard: verify the event actually has prompt()
      if (!('prompt' in e) || typeof (e as BeforeInstallPromptEvent).prompt !== 'function') return;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      if (!getStorage(BANNER_DISMISSED_KEY)) {
        setShowBanner(true);
      }
    };

    const installedHandler = () => {
      setIsInstalled(true);
      setShowBanner(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);

    // iOS: show instructions banner if not dismissed
    if (platform === 'ios' && !getStorage(BANNER_DISMISSED_KEY)) {
      setShowBanner(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [isInstalled, platform]);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    try {
      const result = await deferredPrompt.prompt();
      // Modern Chrome returns outcome from prompt(); legacy uses userChoice
      const outcome = result?.outcome ?? (await deferredPrompt.userChoice).outcome;
      setDeferredPrompt(null);
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setShowBanner(false);
      } else {
        // User dismissed — hide banner too
        setShowBanner(false);
        setStorage(BANNER_DISMISSED_KEY, '1');
      }
      return outcome === 'accepted';
    } catch (err) {
      console.error('[PWA] Install prompt failed:', err);
      setDeferredPrompt(null); // Consumed, clear regardless
      return false;
    }
  }, [deferredPrompt]);

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
    setStorage(BANNER_DISMISSED_KEY, '1');
  }, []);

  return {
    isInstalled,
    canInstall: deferredPrompt !== null,
    showBanner,
    platform,
    install,
    dismissBanner,
  };
}
